import React from 'react';
import { AlertCircle } from 'react-feather';
import { toast, Id } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface ErrorPopupProps {
  message: string;
  stack?: string;
}

// Keep track of active error toasts globally
const activeErrorToasts = new Set<string>();

// Function to truncate text
const truncateText = (text: string, maxLength: number = 100) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export default function ErrorPopup({ message, stack }: ErrorPopupProps) {
  React.useEffect(() => {
    // If this error message already has a toast, don't create another one
    if (activeErrorToasts.has(message)) {
      return;
    }

    const toastId = toast(
      <div className="flex items-start gap-3">
        <div className="mt-1">
          <div className="rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF9900] p-2">
            <AlertCircle className="text-white" size={20} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="font-medium text-white max-w-[280px] truncate">
            {truncateText(message)}
          </h3>
          
          {stack && (
            <p className="text-sm text-white/70 max-w-[280px] truncate">
              {truncateText(stack, 50)}
            </p>
          )}
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        className: "bg-black/90 backdrop-blur-sm border border-[#FF9900]/10",
        progressClassName: "bg-gradient-sonic-horizontal",
        onOpen: () => {
          activeErrorToasts.add(message);
        },
        onClose: () => {
          activeErrorToasts.delete(message);
        }
      }
    );

    return () => {
      toast.dismiss(toastId);
      activeErrorToasts.delete(message);
    };
  }, [message, stack]);

  return null;
}
